import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';
import { formatDate } from './utils';
import useIsMobile from './hooks/useIsMobile';
import HotelSearch from './HotelSearch';

const RaceGuide = ({ raceId, onBack, onViewRace, onSignUp }) => {
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get(`/races/${raceId}`)
      .then(res => setRace(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [raceId]);

  if (loading) {
    return (
      <div style={{ background: '#0B0B10', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: '#8b9cb3' }}>Loading...</p>
      </div>
    );
  }

  if (!race) {
    return (
      <div style={{ background: '#0B0B10', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: '#8b9cb3' }}>Race not found.</p>
      </div>
    );
  }

  // race.date may arrive as "2026-03-01" (date-only) or full ISO
  // ("2026-03-01T00:00:00.000Z"). Appending 'T00:00:00' to a full ISO string
  // produces an invalid Date (NaN), so only append when there's no T.
  const raceDate = race.date
    ? new Date(race.date.includes('T') ? race.date : race.date + 'T00:00:00')
    : null;
  const daysUntil = raceDate ? Math.ceil((raceDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const isUpcoming = daysUntil !== null && daysUntil > 0;
  const locationCity = race.location ? race.location.split(',')[0].trim() : '';
  const raceYear = raceDate && !isNaN(raceDate.getTime()) ? raceDate.getFullYear() : new Date().getFullYear();
  // Many race names already include the year (e.g. "Tokyo Marathon 2026"),
  // so only append if it's missing to avoid "Tokyo Marathon 2026 2026".
  const nameHasYear = race.name && new RegExp(`\\b${raceYear}\\b`).test(race.name);
  const guideTitle = nameHasYear
    ? `${race.name}: Where to Stay, Travel Guide & Course Info`
    : `${race.name} ${raceYear}: Where to Stay, Travel Guide & Course Info`;
  const guideDesc = `Planning to run ${race.name}? Find hotels near the start line, travel tips, course details, pacing strategy, and everything you need for race weekend in ${race.location}.`;

  // Use per-race travel guide content when available, fall back to generic sections
  const sections = race.travel_guide && race.travel_guide.sections
    ? race.travel_guide.sections
    : buildGuideSections(race, daysUntil, isUpcoming, locationCity);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: race.name,
    startDate: race.date,
    location: {
      '@type': 'Place',
      name: race.location,
      address: race.location,
    },
    description: race.description || guideDesc,
    image: race.image,
    url: `https://runrora.com/race/${race.id}/guide`,
    organizer: {
      '@type': 'Organization',
      name: 'RunRora',
      url: 'https://runrora.com',
    },
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
  };

  return (
    <div style={{ background: '#0B0B10', minHeight: '100vh' }}>
      <Helmet>
        <title>{guideTitle}</title>
        <meta name="description" content={guideDesc} />
        <meta property="og:title" content={guideTitle} />
        <meta property="og:description" content={guideDesc} />
        {race.image && <meta property="og:image" content={race.image} />}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://runrora.com/race/${race.id}/guide`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero */}
      <div style={{
        position: 'relative',
        height: isMobile ? '250px' : '350px',
        overflow: 'hidden',
      }}>
        {race.image && (
          <img
            src={race.image}
            alt={race.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.5)' }}
          />
        )}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: isMobile ? '1.5rem' : '2.5rem',
          background: 'linear-gradient(transparent, rgba(11, 11, 16, 0.95))',
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', color: '#8b9cb3',
              cursor: 'pointer', fontSize: '0.9rem', padding: 0, marginBottom: '0.75rem',
            }}
          >
            &larr; Back
          </button>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2.25rem', fontWeight: 700,
            color: '#FFFFFF', margin: 0, lineHeight: 1.2,
          }}>
            {race.name}
          </h1>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            {raceDate && (
              <span style={{ color: '#FF8A3D', fontSize: '0.9rem', fontWeight: 600 }}>
                {formatDate(race.date)}
              </span>
            )}
            <span style={{ color: '#8b9cb3', fontSize: '0.9rem' }}>{race.location}</span>
            <span style={{
              color: '#0B0B10', background: '#FF8A3D', padding: '0.15rem 0.6rem',
              borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
            }}>
              {race.type}
            </span>
          </div>
          {isUpcoming && (
            <p style={{ color: '#FF8A3D', fontSize: '0.9rem', marginTop: '0.5rem', fontWeight: 600 }}>
              {daysUntil} {daysUntil === 1 ? 'day' : 'days'} away
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '800px', margin: '0 auto',
        padding: isMobile ? '1.5rem 1rem' : '2.5rem 1.5rem',
      }}>
        {/* Quick facts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: '1rem', marginBottom: '2.5rem',
        }}>
          {[
            { label: 'Distance', value: race.type },
            { label: 'Location', value: locationCity },
            { label: 'Date', value: raceDate ? formatDate(race.date) : 'TBD' },
            { label: 'Runners on Runrora', value: `${race.going || 0}` },
          ].map((fact, i) => (
            <div key={i} style={{
              background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.12)',
              borderRadius: '12px', padding: '1rem', textAlign: 'center',
            }}>
              <div style={{ color: '#FF8A3D', fontSize: '1.1rem', fontWeight: 700 }}>{fact.value}</div>
              <div style={{ color: '#8b9cb3', fontSize: '0.8rem', marginTop: '0.25rem' }}>{fact.label}</div>
            </div>
          ))}
        </div>

        {/* Hotel search — primary affiliate placement, above the fold after quick facts */}
        <HotelSearch race={race} />

        {/* Guide sections */}
        {sections.map((section, i) => (
          <div key={i} style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '1.25rem', fontWeight: 700, color: '#FFFFFF',
              margin: '0 0 1rem', borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
              paddingBottom: '0.5rem',
            }}>
              {section.title}
            </h2>
            <div style={{ color: '#c4cdd9', fontSize: '0.95rem', lineHeight: 1.7 }}>
              {section.content}
            </div>
          </div>
        ))}

        {/* CTAs */}
        <div style={{
          background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.2)',
          borderRadius: '14px', padding: isMobile ? '1.5rem' : '2rem',
          textAlign: 'center', marginTop: '2rem',
        }}>
          <h3 style={{ color: '#FFFFFF', fontSize: '1.15rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
            Planning to run {race.name}?
          </h3>
          <p style={{ color: '#8b9cb3', fontSize: '0.9rem', margin: '0 0 1.25rem' }}>
            Build your race plan, connect with other runners, and show up ready.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => onViewRace(raceId)}
              style={{
                padding: '0.7rem 1.5rem', background: '#FF8A3D', color: '#0B0B10',
                border: 'none', borderRadius: '25px', fontSize: '0.95rem',
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              View race community
            </button>
            {race.external_url && (
              <a
                href={race.external_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.7rem 1.5rem', background: 'transparent',
                  color: '#FF8A3D', border: '1.5px solid #FF8A3D',
                  borderRadius: '25px', fontSize: '0.95rem', fontWeight: 700,
                  textDecoration: 'none', cursor: 'pointer',
                }}
              >
                Official registration
              </a>
            )}
          </div>
        </div>

        {/* Sign up CTA for logged-out */}
        {onSignUp && (
          <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
            <p style={{ color: '#8b9cb3', fontSize: '0.9rem' }}>
              Don't have an account?{' '}
              <button
                onClick={onSignUp}
                style={{
                  background: 'none', border: 'none', color: '#FF8A3D',
                  cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                Sign up free
              </button>
              {' '}to start planning.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

function buildGuideSections(race, daysUntil, isUpcoming, locationCity) {
  const sections = [];
  const type = (race.type || '').toLowerCase();
  const isMarathon = type.includes('marathon') && !type.includes('half');
  const isHalf = type.includes('half');
  const isUltra = type.includes('ultra');
  const isTriathlon = type.includes('triathlon') || type.includes('ironman');
  const is5k = type.includes('5k');
  const is10k = type.includes('10k');

  // About the race
  if (race.description) {
    sections.push({
      title: `About ${race.name}`,
      content: race.description,
    });
  }

  // Course & what to expect
  if (isMarathon) {
    sections.push({
      title: 'What to expect',
      content: `A full 26.2-mile course through ${locationCity || 'the city'}. Marathon courses vary widely — some are flat and fast, others have significant elevation. Research the course profile and plan your pacing accordingly. The wall typically hits between miles 18-22, so your fueling strategy in the first half matters more than your legs.`,
    });
  } else if (isHalf) {
    sections.push({
      title: 'What to expect',
      content: `A 13.1-mile course through ${locationCity || 'the city'}. Half marathons are the sweet spot — long enough to require real planning, short enough to recover from quickly. If this is your first, aim for even splits. If you've done a few, consider a slight negative split strategy.`,
    });
  } else if (isUltra) {
    sections.push({
      title: 'What to expect',
      content: `An ultramarathon pushes beyond the standard 26.2 miles. Terrain, elevation, and weather become major factors. Aid station strategy is critical — know what each station offers and plan your own supplies accordingly. Walk breaks aren't weakness, they're strategy.`,
    });
  } else if (isTriathlon) {
    sections.push({
      title: 'What to expect',
      content: `A multi-discipline race combining swim, bike, and run. Transitions (T1 and T2) are often called the "fourth discipline" — practice them. Lay out your transition area the night before and have a clear mental checklist for each switch.`,
    });
  } else if (is5k || is10k) {
    sections.push({
      title: 'What to expect',
      content: `A ${is5k ? '3.1' : '6.2'}-mile race through ${locationCity || 'the area'}. ${is5k ? '5Ks reward speed and effort from the start — warm up properly and don\'t hold back.' : '10Ks require more pacing discipline than a 5K. Start controlled and build into the second half.'}`,
    });
  }

  // Pacing strategy
  sections.push({
    title: 'Pacing strategy',
    content: isMarathon
      ? 'The biggest mistake in a marathon is going out too fast. Run the first 10K at a pace that feels easy — almost boring. Your goal is to feel strong at mile 20, not fast at mile 5. Use Runrora\'s pace calculator to build mile-by-mile splits based on your goal time.'
      : isHalf
      ? 'For a half marathon, aim for even or slightly negative splits. Run the first 5K at your target pace — not faster. Save your energy for miles 9-13 when fatigue sets in. Build your split plan on Runrora before race day.'
      : isTriathlon
      ? 'Pace the swim conservatively — save energy for the bike and run. On the bike, stay in your aerobic zone and resist the urge to chase faster riders. The run is where races are won or lost. Use Runrora to plan your target times for each leg.'
      : `Start at a sustainable effort and build from there. Use Runrora's pace calculator to set realistic split targets based on your training.`,
  });

  // Nutrition & fueling
  if (isMarathon || isHalf || isUltra || isTriathlon) {
    sections.push({
      title: 'Nutrition and fueling',
      content: isMarathon
        ? 'Start fueling early — by mile 4 or 5, not when you\'re already hungry. Most runners need 30-60g of carbs per hour during a marathon. Practice your exact fueling plan during long training runs. Never try anything new on race day. Use Runrora\'s fuel planner to map out exactly when and what you\'ll eat.'
        : isUltra
        ? 'Fueling an ultra is a full-time job. You\'ll need a mix of simple and complex carbs, electrolytes, and possibly real food. Know what every aid station offers. Have backup nutrition in your vest. Stomach issues end more ultras than leg issues — train your gut.'
        : isTriathlon
        ? 'Nutrition timing across three disciplines is complex. Take in calories on the bike — it\'s the easiest place to eat. Keep the run fueling simple: gels and electrolytes. Practice race-day nutrition in your brick workouts.'
        : 'For a half marathon, most runners can get by with 1-2 gels and water. If you\'re running over 1:45, plan a gel at miles 5 and 9. Hydrate at every station — small sips, don\'t gulp.',
    });
  }

  // Travel & logistics
  if (race.location) {
    sections.push({
      title: 'Travel and logistics',
      content: `If you're traveling to ${locationCity || race.location} for the race, book accommodation close to the start line or along a transit route to it. Pick up your bib and packet the day before — race morning lines are stressful. Lay out everything the night before: shoes, bib, pins, nutrition, body glide. Use Runrora's travel planner to organize flights, hotels, and your race-morning timeline.`,
    });
  }

  // Race day timeline
  sections.push({
    title: 'Race day timeline',
    content: 'Wake up 3 hours before gun time. Eat your pre-race meal (something you\'ve tested in training). Arrive at the start area 60-90 minutes early. Use the porta-potties early and again 15 minutes before your wave. Do a light warm-up jog 10 minutes before start. Use Runrora\'s race-day timeline planner to build your exact minute-by-minute schedule.',
  });

  // Gear checklist
  sections.push({
    title: 'Gear checklist',
    content: isTriathlon
      ? 'Swim: goggles (plus backup), wetsuit if allowed, tri suit, body glide. Bike: bike (obviously), helmet, cycling shoes, flat repair kit, nutrition on frame. Run: running shoes, hat/visor, race belt. Transitions: towel, sunscreen, spare socks. Use Runrora\'s checklist feature to track everything.'
      : `Race-tested shoes (nothing new on race day), moisture-wicking socks, race outfit you've trained in, body glide for chafing spots, your nutrition (gels, chews, whatever you've practiced with), a hat or visor if sunny, and your phone if you want to track or take photos. Use Runrora's checklist to make sure you don't forget anything.`,
  });

  return sections;
}

export default RaceGuide;
